import { useState } from "react";
import { Package } from "lucide-react";
import { Button, Checkbox } from "@/components/ui";
import { EmptyState } from "@/components/common";
import { notify } from "@/lib/notify";
import { useUpdateProductAttributes } from "../api";
import type { Product } from "@/types";

interface ProductAttributesEditorProps {
  product: Product;
  attributes: { id: string; name: string }[];
}

export function ProductAttributesEditor({
  product,
  attributes,
}: ProductAttributesEditorProps) {
  const updateProductAttributes = useUpdateProductAttributes();

  const [selectedIds, setSelectedIds] = useState<string[]>(
    product.attributes?.map((a) => a.id) ?? [],
  );

  const toggle = (attrId: string) => {
    setSelectedIds((prev) =>
      prev.includes(attrId)
        ? prev.filter((id) => id !== attrId)
        : [...prev, attrId],
    );
  };

  const handleSave = async () => {
    try {
      await updateProductAttributes.mutateAsync({
        id: product.id,
        attributeIds: selectedIds,
      });
      notify.success("Attributes updated");
    } catch (err) {
      notify.fromError(err, "Failed to update attributes");
    }
  };

  if (attributes.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="No attributes"
        description="Create attributes in the Attributes page first"
      />
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-sm text-muted-foreground">
        Select which customization options are available for this product at
        checkout.
      </p>
      <div className="space-y-2">
        {attributes.map((attr) => (
          <div
            key={attr.id}
            className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
              selectedIds.includes(attr.id)
                ? "border-primary bg-primary/5"
                : "hover:bg-muted/50"
            }`}
            onClick={() => toggle(attr.id)}
          >
            <Checkbox checked={selectedIds.includes(attr.id)} />
            <span className="font-medium">{attr.name}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={updateProductAttributes.isPending}
        >
          {updateProductAttributes.isPending ? "Saving..." : "Save Attributes"}
        </Button>
      </div>
    </div>
  );
}
